import { useQuery } from "@tanstack/react-query";

import { PLACES_AUTOCOMPLETE_QUERY_KEY, QUERY_CACHE_LONG_EXPIRATION_MS } from "@config/query";
import { useDebounce } from "@hooks/useDebounce";
import { IPlacePrediction } from "@interfaces/places.interface";
import placesService from "@services/places.service";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * City predictions for LOCATION attributes, proxied by triton so the Google key stays server-side.
 *
 * The CRM calls the Maps JS SDK directly, which can't run here: MV3 forbids extension pages from
 * executing remote code, and the SDK delivers each result set as a remote script. Triton returns
 * the same legacy payload, so the dropdown renders identically.
 */
export function useLocationPredictions(query: string) {
  const debouncedQuery = useDebounce(query.trim(), DEBOUNCE_MS);

  const { data } = useQuery({
    queryKey: [PLACES_AUTOCOMPLETE_QUERY_KEY, debouncedQuery],
    queryFn: () => placesService.getAutocomplete(debouncedQuery),
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
    // Repeats of a prefix already typed resolve from cache instead of a round trip.
    staleTime: QUERY_CACHE_LONG_EXPIRATION_MS,
  });

  const predictions: IPlacePrediction[] = data?.predictions ?? [];

  return { predictions };
}
